import { KeyValuePipe } from '@angular/common';
import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideSave } from '@ng-icons/lucide';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { REFERENTIEL_TYPE } from '../models/référentiel-type.enum';
import { ÉQUIPE_RESPONSABLE } from '../models/équipe-responsable.enum';
import { ConvertReferentielTypeToStringPipe } from '../pipes/convert-referentiel-type-to-string-pipe';
import { ReferentielCreateRequest } from '../request/create-request';
import { ReferentielStore } from '../store/referentiel.store';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { INSTITUTS_MÉTIER } from '@shared/models/instituts-métier';

@Component({
  selector: 'app-referentiels-form',
  imports: [
    ReactiveFormsModule,
    ConvertReferentielTypeToStringPipe,
    HlmButtonImports,
    KeyValuePipe,
    HlmIconImports,
    HlmInputImports,
    HlmFieldImports,
    BrnSelectImports,
    HlmSelectImports,
    LabelRequiredDirective,
  ],
  providers: [provideIcons({ lucideArrowLeft, lucideSave })],
  templateUrl: './referentiels-form.component.html',
})
export class RéférentielsFormComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #store = inject(ReferentielStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);

  readonly idCartographie = input.required<string>();

  form = this.#fb.group({
    nom: [null, [Validators.required]],
    referentielType: [null, [Validators.required]],
    equipeResponsable: [null],
    codeMetier: [null],
    trigrammeMetier: [null],
  });

  readonly referentielTypes = REFERENTIEL_TYPE;
  readonly institutsMetier = INSTITUTS_MÉTIER;

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
    this.referentielType.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value: REFERENTIEL_TYPE) => {
        if (value === REFERENTIEL_TYPE.METIER) {
          this.equipeResponsable.setValidators([Validators.required]);
          this.codeMetier.setValidators([Validators.required, Validators.minLength(6), Validators.maxLength(6)]);
          this.trigrammeMetier.setValidators([Validators.required, Validators.minLength(3), Validators.maxLength(3)]);
        } else {
          this.equipeResponsable.setValidators(null);
          this.codeMetier.setValidators(null);
          this.trigrammeMetier.setValidators(null);
        }
      });
  }

  onSubmit() {
    toast.dismiss();

    const toastrId = uuidv7();

    if (this.form.invalid) {
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message: 'Merci de renseigner les champs marqués comme obligatoires.',
          titre: 'Erreur',
          id: toastrId,
        },
        id: toastrId,
      });
      this.form.markAllAsTouched();
      return;
    }

    const référentiel: ReferentielCreateRequest = {
      nom: this.nom.value,
      referentielType: this.referentielType.value,
      equipeResponsable: this.equipeResponsable.value,
      estActif: true,
      idCartographie: this.idCartographie(),
      codeMetier: this.codeMetier.value,
      trigrammeMetier: this.trigrammeMetier.value,
    };

    switch (référentiel.referentielType) {
      case REFERENTIEL_TYPE.COMPAGNONNIQUE:
        référentiel.equipeResponsable = ÉQUIPE_RESPONSABLE.COMPAGNONNIQUE;
        break;
      case REFERENTIEL_TYPE.FORMATEUR:
        référentiel.equipeResponsable = ÉQUIPE_RESPONSABLE.FORMATEUR;
        break;
      case REFERENTIEL_TYPE.SCIENCES_ET_CULTURE:
        référentiel.equipeResponsable = ÉQUIPE_RESPONSABLE.SCIENCES_ET_CULTURE;
        break;
      case REFERENTIEL_TYPE.TRANSVERSE:
        référentiel.equipeResponsable = ÉQUIPE_RESPONSABLE.TRANSVERSE;
        break;
    }

    this.#store.créer({
      data: référentiel,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: `Le référentiel '${this.nom.value}' a bien été créé !`,
            titre: `Référentiel '${this.nom.value}' créé`,
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
    });
  }

  get nom() {
    return this.form.get(FormControlNames.NOM) as FormControl;
  }

  get referentielType() {
    return this.form.get(FormControlNames.REFERENTIEL_TYPE) as FormControl;
  }

  get equipeResponsable() {
    return this.form.get(FormControlNames.EQUIPE_RESPONSABLE) as FormControl;
  }

  get codeMetier() {
    return this.form.get(FormControlNames.CODE_METIER) as FormControl;
  }

  get trigrammeMetier() {
    return this.form.get(FormControlNames.TRIGRAMME_METIER) as FormControl;
  }
}

export enum FormControlNames {
  NOM = 'nom',
  REFERENTIEL_TYPE = 'referentielType',
  EQUIPE_RESPONSABLE = 'equipeResponsable',
  CODE_METIER = 'codeMetier',
  TRIGRAMME_METIER = 'trigrammeMetier',
}
